import { z } from "zod";

/**
 * Printing without a click: a small agent installed on a warehouse computer polls the ERP for
 * print jobs and sends each PDF to a printer of that computer. The ERP never talks to a printer.
 */
export const PRINT_KINDS = ["label", "shipment_delivery_note", "order_delivery_note"] as const;
export type PrintKind = (typeof PRINT_KINDS)[number];
/** A target is chosen per document family: labels on the label printer, delivery notes on the office one. */
export const PRINT_TARGETS = ["label", "deliveryNote"] as const;
export type PrintTarget = (typeof PRINT_TARGETS)[number];
export const PRINT_JOB_STATUSES = ["pending", "printing", "done", "failed"] as const;
export type PrintJobStatus = (typeof PRINT_JOB_STATUSES)[number];

export const targetOfKind = (kind: PrintKind): PrintTarget => (kind === "label" ? "label" : "deliveryNote");

/** An agent is considered online while it polled less than this long ago (it polls every few seconds). */
export const PRINT_AGENT_ONLINE_MS = 90_000;
/** A job claimed by an agent that never reported back goes back to the queue after this long. */
export const PRINT_JOB_STALE_MS = 3 * 60_000;
export const PRINT_JOB_MAX_ATTEMPTS = 3;
export const PRINT_MAX_COPIES = 10;

/** The executables offered for download, built with the API image (apps/print-agent/scripts/build.ts). */
export const PRINT_AGENT_PLATFORMS = ["windows", "macos-apple-silicon", "macos-intel", "linux"] as const;
export type PrintAgentPlatform = (typeof PRINT_AGENT_PLATFORMS)[number];
export const PRINT_AGENT_FILES: Record<PrintAgentPlatform, string> = {
  windows: "erp-print-agent-windows.exe",
  "macos-apple-silicon": "erp-print-agent-macos-apple-silicon",
  "macos-intel": "erp-print-agent-macos-intel",
  linux: "erp-print-agent-linux",
};

const printerName = z.string().trim().min(1, "errors.field.required").max(200, "errors.field.tooLong");

export const printAgentCreateSchema = z.object({
  name: z.string().trim().min(1, "errors.field.required").max(80, "errors.field.tooLong"),
});
export type PrintAgentCreateData = z.output<typeof printAgentCreateSchema>;

/** Where each document family goes; null means "no automatic printing, the browser dialog as before". */
export const printRouteSchema = z.object({ agentId: z.uuid(), printer: printerName });
export type PrintRoute = z.output<typeof printRouteSchema>;
export const autoPrintSchema = z.object({
  label: printRouteSchema.nullable(),
  deliveryNote: printRouteSchema.nullable(),
});
export type AutoPrintData = z.output<typeof autoPrintSchema>;

export const printJobCreateSchema = z.object({
  kind: z.enum(PRINT_KINDS),
  entityId: z.uuid(),
  copies: z.number().int().min(1).max(PRINT_MAX_COPIES).optional(),
});
export type PrintJobCreateData = z.output<typeof printJobCreateSchema>;

/** What the agent sends on each poll: the printers it sees, its version; it gets its claimed jobs back. */
export const printAgentPollSchema = z.object({
  printers: z.array(printerName).max(50),
  version: z.string().trim().max(40).optional(),
  platform: z.string().trim().max(40).optional(),
});
export type PrintAgentPollData = z.output<typeof printAgentPollSchema>;

export const printAgentReportSchema = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true) }),
  z.object({ ok: z.literal(false), error: z.string().trim().min(1).max(1000) }),
]);
export type PrintAgentReportData = z.output<typeof printAgentReportSchema>;

/** The names the agent lists, cleaned: one per line, blanks and duplicates dropped. */
export function normalizePrinters(names: readonly string[]): string[] {
  const seen = new Set<string>();
  for (const raw of names) {
    const name = raw.trim();
    if (name && !seen.has(name)) seen.add(name);
  }
  return [...seen].slice(0, 50);
}
